import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send } from "lucide-react";

export const CommentsDialog = ({ selectedPost, comments, newComment, onCommentChange, onAddComment, onClose }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={!!selectedPost} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('community.comments')}</DialogTitle>
        </DialogHeader>
        {selectedPost && (
          <div>
            <div className="p-4 bg-muted/20 rounded mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={selectedPost.user_picture} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">
                    {selectedPost.user_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{selectedPost.user_name}</span>
              </div>
              <p className="text-sm">{selectedPost.content}</p>
            </div>

            <ScrollArea className="h-[200px] mb-4">
              {comments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('community.noPosts')}</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div key={comment.comment_id} className="flex gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={comment.user_picture} />
                        <AvatarFallback className="bg-primary/20 text-primary text-xs">
                          {comment.user_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 bg-muted/10 rounded p-2">
                        <p className="text-xs font-medium">{comment.user_name}</p>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="flex gap-2">
              <Input
                placeholder={t('community.comment')}
                value={newComment}
                onChange={(e) => onCommentChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && onAddComment()}
                className="bg-transparent border-border"
              />
              <Button onClick={onAddComment} className="bg-primary text-primary-foreground">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const MessageDialog = ({ open, onOpenChange, selectedUser, newMessage, onMessageChange, onSend }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle>{t('community.sendMessage')} {selectedUser?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            placeholder={t('community.messagePlaceholder')}
            value={newMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            className="bg-transparent border-border min-h-[100px]"
          />
          <Button
            onClick={onSend}
            disabled={!newMessage.trim()}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Send className="w-4 h-4 mr-2" />
            {t('community.send')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
